/* eslint-disable jsx-a11y/anchor-is-valid, max-lines, react/no-children-prop */
import { paramCase } from "change-case";
import * as React from "react";
import { Helmet } from "react-helmet";
import { classNames } from "@plinknz/tah-website-elements";

import { Button } from "../components/button";
import { Hero } from "../components/hero";
import { Loader } from "../components/loader";
import { BlockQuote } from "../components/content-blocks/blockquote";
import { CallToActionBlock } from "../components/content-blocks/call-to-action-block";
import { DownloadBlock } from "../components/content-blocks/download-block";
import { GalleryBlock } from "../components/content-blocks/gallery";
import { GoogleCalendar } from "../components/content-blocks/google-calendar";
import { LinksBlock } from "../components/content-blocks/links-block";
import { NewsGridBlock } from "../components/content-blocks/news-grid-block";
import { PanuiGridBlock } from "../components/content-blocks/panui-grid-block";
import { PictureList } from "../components/content-blocks/picture-list";
import { YoutubePlayerContentBlock } from "../components/content-blocks/youtube-player";
import { Waiata } from "../components/content-blocks/waiata";
import { AddressFinder } from "../components/form/address-finder";
import { Checkbox } from "../components/form/checkbox";
import { DatePicker } from "../components/form/datepicker";
import { Form } from "../components/form/form";
import { SignatureCanvas } from "../components/form/signature-canvas";
import { Radio, RadioColumn } from "../components/form/radio";
import { Dropdown } from "../components/form/dropdown";
import { Whakapapa } from "../components/form/whakapapa/whakapapa";
import { Field } from "../components/form/field";

interface StyleGuideSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string;
  contain?: boolean;
}

const StyleGuideSection = ({
  title,
  subtitle,
  children,
  contain = false,
  className,
  ...props
}: StyleGuideSectionProps) => (
  <section
    id={paramCase(title)}
    className={classNames(
      "style-guide-section",
      { "constrain-width": contain },
      className
    )}
    {...props}
  >
    <div
      className={`style-guide-section-heading ${
        !contain ? "constrain-width" : ""
      }`}
    >
      <h1 className="heading-2 primary-color rotate">
        {title}{" "}
        {subtitle && (
          <small className="style-guide-section-subtitle">| {subtitle}</small>
        )}
      </h1>
      <hr className="style-guide-section-break" />
    </div>
    <div className="style-guide-section-content">{children}</div>
  </section>
);

interface SwatchProps {
  title: string;
  value: string;
  outline?: boolean;
}
const Swatch = ({ title, value, outline = false }: SwatchProps) => (
  <div className={`swatch small ${outline ? "outline" : ""} colour-${value}`}>
    <div className="swatch-inner">
      <p className="swatch-title">{title}</p>
      <div role="group" className="swatch-variations">
        <span className="swatch-variation tint-1" />
        <span className="swatch-variation shade-1" />
      </div>
    </div>
  </div>
);

export const StyleGuide = () => {
  return (
    <div className="style-guide">
      <Helmet>
        <title>Style Guide - Rangitāne</title>
      </Helmet>

      <Hero
        title="Tauira Tāera / Style Guide"
        subtitle="A working site web design style guide for Rangitāne."
      />

      <StyleGuideSection title="Tae" subtitle="Colours" contain>
        <div className="style-guide-swatches" role="group">
          <Swatch title="Primary" value="primary" />
          <Swatch title="Secondary" value="secondary" />
          <Swatch title="Tertiary" value="tertiary" />
          <Swatch title="Text" value="body" />
          <Swatch title="Shade" value="dark" />
          <Swatch title="Background" value="light" outline />
        </div>
      </StyleGuideSection>

      <StyleGuideSection title="Momotuhi" subtitle="Type" contain>
        <h1>Header 1</h1>
        <h2>Header 2</h2>
        <h3>Header 3</h3>
        <h4>Header 4</h4>
        <h5>Header 5</h5>
        <h6>Header 6</h6>

        <p>E noho nei au i tōku taumata kōrero, i Te Tapuae-o-Uenuku,</p>
        <p>
          Ka kai ōku mata ki waho, ki a Raukawakawa Moana, te ara tēnā i ū mai
          ai tōku tupuna ki Anamāhanga, e ko Te Huataki e!
        </p>
        <p>
          Whakawhiti atu, whakawhiti mai, auraki mai ana ko ngā mana
          whakatiketike i ahau, ko Te Whakamana, ko Tūkauae, ko Te Rerewa, ko Te
          Heiwi, nā te moenga i a Ngāi Tara, i a Ngāti Māmoe, ko noho ko te
          waikanaetanga, poua ana ko te mana o Rangitāne ki Te Tauihu o Te
          Waka-a-Māui e!
        </p>
        <p>
          Māreparepa mai ana ngā wai-rau o Ruatere, ngā rohe kōreporepo, te wai
          māhitihiti o ngā mātua tūpuna, ko Te Wairau e kāpikopiko ana i te
          hōkikitanga, te whaonga mai i te whenua ka rere ki te pūruatanga, kō
          ōna pūahatanga kei Te Koko o Kupe, kei Te Pokohiwi o Kupe.
        </p>
      </StyleGuideSection>

      <StyleGuideSection title="Waiata" subtitle="Songs" contain>
        <Waiata
          data={{
            __typename: "ComponentContentBlocksWaiata",
            title: "Kupe",
            timed_kupu:
              "0.7:Ehara:1.7:taku:2.2:mana:2.7:i:2.9:te:3.2:mana:3.7:kore:4.2:noa::4.7:Engari:5.6:taku:6.1:mana:6.5:he:6.9:mana:7.6:tuku:8.1:iho::8.7:I:9.2:takea:10.2:mai:10.7:i:10.9:tōku:11.6:tupuna::12.7:I:12.9:ā:13.1:Kupe:13.7:ariki:14.8:e::16.8:Ko:17.1:Kupe:17.6:te:18.1:kaiwhakatere:19.7:moana::20.6:Ko:21.2:Kupe:21.7:te:22:kaiwhakatōmene:24.2:whenua::25.2:Ko:25.8:Kupe:26.3:te:26.5:tangata:27.2:Ko:27.8:Matahourua:29.3:te:29.7:waka::30:Anei:31.3:ngā:31.9:uri:32.5:e:32.8:tū:33.1:nei::36.2:E:36.4:tū:36.6:nei",
            audio_file: {
              name: "Kupe Waiata.mp3",
              url:
                "https://images.prod.ngatikuia.iwi.nz/Kupe_Waiata_40340b779d.mp3",
              size: "1518.98",
              ext: ".mp3",
            },
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection title="Rārangi" subtitle="Lists" contain>
        <h4>Unordered</h4>
        <ul>
          <li>
            Ki te whenua <strong>kua kite ratou kei to mawehe atu </strong> o
            ratou whenua i a ratou, e tika ana ano kia.
          </li>
          <li>
            Kupu i reira ko te whenua <em>ki te Kai Tuhi </em> facilisis at iwi
            ki a ratou. He nui rapea nga korero mo nga rori.
          </li>
          <li>
            Ko te utu mo te Waka, Maori i te tau ka te 10s., he mea utu ki mua.
            Ka tukuna atu i te meera.
          </li>
          <li>
            Me ka tukua mai e ia aua moni ki te Kai Tuhi ki Poneke nei. Ta ratou
            kupu i reira ko te whenua kia whakawakia kia{" "}
            <a href="#">kia watea ratou i nga raruraru</a> whakawakia Kua tonoa
            te Paremete e te Kawana kia hui mai i te 2 o nga haora o te awatea o
            te Taitei.
          </li>
        </ul>

        <h4>Ordered</h4>
        <ol className="style-guide-section-list">
          <li>
            Kia whakawakia kia{" "}
            <strong>E ngari me tino mohio ano te Muera</strong> ki te tangata
            kei to mawehe atu.
          </li>
          <li>
            Kei to mawehe atu <em>nga tikanga pera</em> ki te tangata Ka tukuna
            atu i te meera ki te tangata e hiahia ana hiahia.
          </li>
          <li>
            Iwi ki a ratou. He nui rapea nga korero mo nga rori ki te tangata e
            hiahia ana.
          </li>
          <li>
            Iwi ki a ratou. He nui rapea nga korero mo nga rori, mo nga
            ruritanga hoki, mo te aha atu; otira ko te tino kupu tena,{" "}
            <a href="#">Ta ratou kupu i reira ko te whenua</a> korero iwi ki a
            ratou. He nui rapea nga korero mo nga rori ki te mahi i nga mahi o
            te motu.
          </li>
        </ol>
      </StyleGuideSection>

      <StyleGuideSection
        title="Pātene"
        subtitle="Buttons"
        contain
        className="style-guide-buttons"
      >
        <Button sizeStyle="large">
          Generic Button
          <Button.Icon />
        </Button>
        <Button>Button</Button>
        <Button sizeStyle="small">Button</Button>
        <br />
        <br />

        <Button variant="primary" sizeStyle="large">
          Primary Button
        </Button>
        <Button variant="primary">
          Primary Button <Button.Icon />
        </Button>
        <Button variant="primary">Primary Button</Button>
        <Button variant="primary" sizeStyle="small">
          Primary Button
        </Button>
        <br />
        <br />

        <Button variant="secondary" sizeStyle="large">
          Secondary Button
        </Button>
        <Button variant="secondary">Secondary Button</Button>
        <Button variant="secondary" sizeStyle="small">
          Secondary Button <Button.Icon />
        </Button>
        <br />
        <br />

        <Button variant="tertiary" sizeStyle="large">
          Tertiary Button <Button.Icon />
        </Button>
        <Button variant="tertiary">Tertiary Button</Button>
        <Button variant="tertiary" sizeStyle="small">
          Tertiary Button
        </Button>
        <br />
        <br />

        <Button disabled>Disabled Button</Button>
        <Button disabled>Disabled Button</Button>
      </StyleGuideSection>

      <StyleGuideSection title="Tohutoro" subtitle="Blockquote" contain>
        <BlockQuote
          data={{
            __typename: "ComponentContentBlocksQuote",
            text: `
                            Rangitāne tāngata rau, Rangitāne-nui-a-rangi. Tini whetū ki
                            te rangi, ko Rangitāne ki te whenua.`,
          }}
        />
        <div className="content" />
      </StyleGuideSection>

      <StyleGuideSection title="Whakauta" subtitle="Loading" contain>
        <div className="style-guide-loaders">
          <div>
            <Loader size="large" />
            <p>Large</p>
          </div>
          <div>
            <Loader />
            <p>Normal</p>
          </div>
          <div>
            <Loader size="small" />
            <p>Small</p>
          </div>
        </div>
      </StyleGuideSection>

      <StyleGuideSection title="Tikiake" subtitle="Downloads" contain>
        <h3>Single file with description and no image</h3>
        <DownloadBlock
          data={{
            __typename: "ComponentContentBlocksDownloadFiles",
            id: "1",
            heading: "Rangitāne Annual Reports",
            description:
              "Rangitāne annual report for the financial year 2020-2021",
            files: [
              {
                id: "1",
                name: "Annual Report 2024",
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 2680900.21,
                },
              },
            ],
          }}
        />

        <h3>Single file with image and no description</h3>
        <DownloadBlock
          data={{
            __typename: "ComponentContentBlocksDownloadFiles",
            id: "1",
            heading: "Annual Report 2021",
            description: "",
            files: [
              {
                id: "1",
                name: "Annual Report 2020",
                image: {
                  url: "/images/defaults/image2.jpg",
                },
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 2680900.21,
                },
              },
            ],
          }}
        />

        <h3>Single file with image and description</h3>
        <DownloadBlock
          data={{
            __typename: "ComponentContentBlocksDownloadFiles",
            id: "1",
            heading: "Ammendments to Rangitāne Deed",
            description:
              "Rangitāne annual report for the financial year 2020-2021",
            files: [
              {
                id: "1",
                name: "Ammendments to Rangitāne Deed",
                image: {
                  url: "/images/defaults/image2.jpg",
                },
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 2680900.21,
                },
              },
            ],
          }}
        />

        <h3>Two files, one with image, one using the default image</h3>
        <DownloadBlock
          data={{
            __typename: "ComponentContentBlocksDownloadFiles",
            id: "1",
            heading: "Rangitāne Annual Reports",
            description:
              "Rangitāne annual report for the financial year 2021- 2022",
            files: [
              {
                id: "1",
                name: "Annual Report 2020",
                image: {
                  url: "/images/defaults/image3.jpg",
                },
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 2680900.21,
                },
              },
              {
                id: "2",
                name: "Default Image",
                file: {
                  name: "Default Image",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 13809.66,
                },
              },
            ],
          }}
        />

        <h3>Three files, no images</h3>
        <DownloadBlock
          data={{
            __typename: "ComponentContentBlocksDownloadFiles",
            id: "1",
            heading: "Rangitāne Annual Reports",
            description:
              "Rangitāne annual report for the financial year 2020- 2022",
            files: [
              {
                id: "1",
                name: "Annual Report 2020",
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 2680900.21,
                },
              },
              {
                id: "2",
                file: {
                  name: "Annual Report 2021",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 13809.66,
                },
              },
              {
                id: "3",
                file: {
                  name: "Annual Report 2022",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 13809.66,
                },
              },
            ],
          }}
        />

        <h3>Three files with images</h3>
        <DownloadBlock
          data={{
            __typename: "ComponentContentBlocksDownloadFiles",
            id: "1",
            heading: "Rangitāne Annual Reports",
            description:
              "Rangitāne annual report for the financial year 2021- 2022",
            files: [
              {
                id: "1",
                name: "Annual Report 2020",
                image: {
                  url: "/images/defaults/image4.jpg",
                },
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 2680900.21,
                },
              },
              {
                id: "2",
                name: "Annual Report 2019",
                image: {
                  url: "/images/defaults/image3.jpg",
                },
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 13809.66,
                },
              },
              {
                id: "3",
                name: "Default Image",
                file: {
                  name: "Rangitāne Annual Report",
                  url:
                    "https://www.rangitane.org.nz/wp-content/uploads/2024/12/Annual-Report-2024-DIGITAL.pdf",
                  size: 13809.66,
                },
              },
            ],
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection
        title="Page Grid"
        subtitle="Tukutuku Whārangi"
        contain={false}
      >
        <NewsGridBlock
          children={[]}
          data={{
            __typename: "ComponentContentBlocksPageGrid",
            id: "1",
            title: "Pages can be specified or use the page's child pages",
            pages: [
              {
                title: "Roadshows",
                slug: "/history",
                cover_image: {
                  url: "/images/defaults/image2.jpg",
                },
              },
              {
                title: "Default Image",
                slug: "/history",
                cover_image: null,
              },
              {
                title: "Wānanga Rangatahi",
                slug: "/history",
                cover_image: {
                  url: "/images/defaults/image5.jpg",
                },
              },
              {
                title: "Matariki",
                slug: "/history",
                cover_image: {
                  url: "/images/defaults/image4.jpg",
                },
              },
              {
                title: "Hui-ā-tau",
                slug: "/history",
                cover_image: {
                  url: "/images/defaults/image2.jpg",
                },
              },
              {
                title: "Hui Kaimoana",
                slug: "/history",
                cover_image: {
                  url: "/images/defaults/image3.jpg",
                },
              },
            ],
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection title="Whakahauhau" subtitle="Call To Action">
        <CallToActionBlock
          data={{
            __typename: "ComponentContentBlocksCallToAction",
            id: "1",
            callToActionTitle: "Register with Rangitāne",
            description: "Rēhita mai",
            action: {
              label: "Join Us",
              link: { slug: "/link" },
              member_link: { slug: "/link" },
            },
            background: {
              url: "/images/defaults/image2.jpg",
            },
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection
        title="Wātaka Kūkara"
        subtitle="Google Calendar"
        contain
      >
        <GoogleCalendar
          data={{
            __typename: "ComponentContentBlocksGoogleCalendar",
            id: "1",
            calendarId:
              "c_ec76681deb8bb37c20d91cd330d4d1f53fab988b7e18d3e5602e77801225b704",
            active: "true",
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection title="Tiriata" subtitle="YouTube">
        <YoutubePlayerContentBlock
          data={{
            __typename: "ComponentContentBlocksYouTubePlayer",
            id: "1",
            title: "Video Title",
            description:
              "Videos to keep people on your website longer and engage them with your content. People work with people, and video helps people get to know, like and trust you. Videos keep your audience interested, no matter your product or service. Videos are fast and convenient.",
            youtube_url: "https://youtu.be/YpJqYIl1BK8",
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection
        title="News Grid"
        subtitle="Tukutuku Kawepūrongo"
        contain={false}
      >
        <PanuiGridBlock
          data={{
            __typename: "ComponentContentBlocksPanuiGrid",
            title: "Latest news will be displayed first",
            pages: [
              {
                title: "Roadshows",
                slug: "history",
                published_at: "2022-09-04T00:00:00.000Z",
                cover_image: {
                  url: "/images/defaults/image2.jpg",
                },
              },
              {
                title: "Default Image",
                slug: "history",
                published_at: "2022-09-03T00:00:00.000Z",
                cover_image: null,
              },
              {
                title: "Wānanga Rangatahi",
                slug: "history",
                published_at: "2022-09-05T00:00:00.000Z",
                cover_image: {
                  url: "/images/defaults/image5.jpg",
                },
              },
              {
                title: "Matariki",
                slug: "history",
                published_at: "2022-09-06T00:00:00.000Z",
                cover_image: {
                  url: "/images/defaults/image4.jpg",
                },
              },
              {
                title: "Hui-ā-tau",
                slug: "history",
                published_at: "2022-09-07T00:00:00.000Z",
                cover_image: {
                  url: "/images/defaults/image2.jpg",
                },
              },
              {
                title: "Hui Kaimoana",
                slug: "history",
                published_at: "2022-09-08T00:00:00.000Z",
                cover_image: {
                  url: "/images/defaults/image3.jpg",
                },
              },
            ],
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection title="Tukutuku Tāngata" subtitle="Profile Grid">
        <PictureList
          data={{
            __typename: "ComponentContentBlocksPictureList",
            id: "2",
            profileHeading: "Te Poari",
            profiles: [
              {
                id: "2",
                title: "Calvin Hart",
                subtitle: "Co-Chairperson",
                description:
                  "Ekore e tika kia noho he Māori rawakore ki tēnei whenua; ehara tēnei i te mea e ora ai tātou e tika ai rānei rātou . E mea ana tātou kia noho te Māori i runga i te rangimārie pēnei me tātou , ā e kī ana ahau mehemea tērā tētahi iwi i whiwhi nui ki te whenua kua kite rātou kei tō mawehe atu o rātou whenua i a rātou , e tika ana anō kia pōuri rātou.",
                background_colour: null,
                picture: {
                  name: "calvin_hart_profile.jpg",
                  alternativeText: "Calvin Hart",
                  url: "/images/defaults/person1.jpg",
                },
              },
              {
                id: "2",
                title: "Janis De Thierry",
                subtitle: "Co-Chairperson",
                description:
                  "Ko te utu mō te Waka, Māori i te tau ka te 10s., he mea utu ki mua. Ka tukuna atu i te mēra ki te tangata e hiahia ana me ka tukua mai e ia aua moni ki te Kai Tuhi ki Pōneke nei. Kua tonoa te Pāremete e te Kāwana kia hui mai i te 2 o ngā hāora o te awatea o te Taitei, te 15 o Hune, ki te mahi i ngā mahi o te motu.",
                background_colour: null,
                picture: {
                  name: "janis_di_thierry_profile.jpg",
                  alternativeText: "Clavin Hart",
                  url: "/images/defaults/person2.jpg",
                },
              },
              {
                id: "2",
                title: "Wirihana de Thierry-Lukitau",
                subtitle: "Trustee",
                description:
                  "Ko te utu mō te Waka, Māori i te tau ka te 10s., he mea utu ki mua. Ka tukuna atu i te mēra ki te tangata e hiahia ana me ka tukua mai e ia aua moni ki te Kai Tuhi ki Pōneke nei. Kua tonoa te Pāremete e te Kāwana kia hui mai i te 2 o ngā hāora o te awatea o te Taitei, te 15 o Hune, ki te mahi i ngā mahi o te motu.",
                background_colour: null,
                picture: {
                  name: "wirihana_profile.jpg",
                  alternativeText: "Wirihana de Thierry-Lukitau",
                  url: "/images/defaults/person1.jpg",
                },
              },
              {
                id: "2",
                title: "Haysley MacDonald",
                subtitle: "Trustee",
                description:
                  "Ko te utu mō te Waka, Māori i te tau ka te 10s., he mea utu ki mua. Ka tukuna atu i te mēra ki te tangata e hiahia ana me ka tukua mai e ia aua moni ki te Kai Tuhi ki Pōneke nei. Kua tonoa te Pāremete e te Kāwana kia hui mai i te 2 o ngā hāora o te awatea o te Taitei, te 15 o Hune, ki te mahi i ngā mahi o te motu.",
                background_colour: null,
                picture: {
                  name: "haysley_profile.jpg",
                  alternativeText: "Haysley MacDonald",
                  url: "/images/defaults/person2.jpg",
                },
              },
              {
                id: "2",
                title: "Peter Meihana",
                subtitle: "Trustee",
                description:
                  "Ko te utu mō te Waka, Māori i te tau ka te 10s., he mea utu ki mua. Ka tukuna atu i te mēra ki te tangata e hiahia ana me ka tukua mai e ia aua moni ki te Kai Tuhi ki Pōneke nei. Kua tonoa te Pāremete e te Kāwana kia hui mai i te 2 o ngā hāora o te awatea o te Taitei, te 15 o Hune, ki te mahi i ngā mahi o te motu.",
                background_colour: null,
                picture: {
                  name: "peter.jpg",
                  alternativeText: "Peter Meihana",
                  url: "/images/defaults/person1.jpg",
                },
              },
              {
                id: "2",
                title: "Keelan Walker",
                subtitle: "Trustee",
                description:
                  "Ko te utu mō te Waka, Māori i te tau ka te 10s., he mea utu ki mua. Ka tukuna atu i te mēra ki te tangata e hiahia ana me ka tukua mai e ia aua moni ki te Kai Tuhi ki Pōneke nei. Kua tonoa te Pāremete e te Kāwana kia hui mai i te 2 o ngā hāora o te awatea o te Taitei, te 15 o Hune, ki te mahi i ngā mahi o te motu.",
                background_colour: null,
                picture: {
                  name: "keelan.jpg",
                  alternativeText: "Keelan Walker",
                  url: "/images/defaults/person2.jpg",
                },
              },
              {
                id: "2",
                title: "Judith MacDonald",
                subtitle: "Trustee",
                description:
                  "Ko te utu mō te Waka, Māori i te tau ka te 10s., he mea utu ki mua. Ka tukuna atu i te mēra ki te tangata e hiahia ana me ka tukua mai e ia aua moni ki te Kai Tuhi ki Pōneke nei. Kua tonoa te Pāremete e te Kāwana kia hui mai i te 2 o ngā hāora o te awatea o te Taitei, te 15 o Hune, ki te mahi i ngā mahi o te motu.",
                background_colour: null,
                picture: {
                  name: "judith.jpg",
                  alternativeText: "Judith Macdonald",
                  url: "/images/defaults/person1.jpg",
                },
              },
            ],
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection title="Hononga" subtitle="Links">
        <LinksBlock
          data={{
            __typename: "ComponentContentBlocksLinks",
            id: "123",
            title: "Helpful Links",
            description:
              "Info and resources specifically for whanau. He nui rapea nga korero mo nga rori, mo nga ruritanga hoki, Latest from Rangitāne. Information on Health. Iwi ki a ratou. Looking after mental health and wellbeing during covid. mo te aha atu; otira ko te tino kupu",
            links: [
              {
                title: "Rangitāne Honey",
                url:
                  "https://www.aceaotearoa.org.nz/news-and-resources/news/ng%C4%81ti-kuia-building-wh%C4%81nau-skills",
              },
              {
                title: "Covid Advice for Māori",
                url:
                  "https://www.aceaotearoa.org.nz/news-and-resources/news/ng%C4%81ti-kuia-building-wh%C4%81nau-skills",
              },
              {
                title: "Healthline",
                url:
                  "https://www.aceaotearoa.org.nz/news-and-resources/news/ng%C4%81ti-kuia-building-wh%C4%81nau-skills",
              },
            ],
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection
        title="Kete Whakaahua"
        subtitle="Gallery"
        contain={false}
      >
        <GalleryBlock
          data={{
            __typename: "ComponentContentBlocksImageGallery",
            id: "1",
            heading: "Kotahi",
            galleryId: "123456",
            description:
              "Ko te moemoea a Maui kia haere ngatahi ai ratou ko ona tuakana ki te hii ika. I te hokinga mai o ona tuakana ki tatahi, ka kii atu a Maui, “ka taea e au te haramai i to koutou na taha ki te hii ika?” Engari, ko te whakautu o ona tuakana ki a ia ano, “Kao, he rangatahi noa iho koe. Kaore he wahi mau kei te waka nei, na reira me noho tau ki tatahi ke”.",
            images: [
              {
                heading: "Rua",
                description:
                  "Ko te moemoea a Maui kia haere ngatahi ai ratou ko ona tuakana ki te hii ika. ratou. He nui rapea nga korero mo nga roriratou. He nui rapea nga korero mo nga roriratou. He nui rapea nga korero mo nga rori",
                image: {
                  alternativeText: "",
                  url: "/images/defaults/image2.jpg",
                  height: 800,
                  width: 800,
                },
              },
              {
                heading: "Wha",
                description:
                  "Ko te moemoea a Maui kia haere ngatahi ai ratou ko ona tuakana ki te hii ika.",
                image: {
                  alternativeText: "",
                  url: "/images/defaults/image5.jpg",
                  height: 800,
                  width: 800,
                },
              },
              {
                heading: "Tahi",
                description:
                  "Ko te moemoea a Maui kia haere ngatahi ai ratou Maui kia haere ngatahi tuakana ki te hii ika.",
                image: {
                  alternativeText: "",
                  url: "/images/defaults/image4.jpg",
                  height: 800,
                  width: 800,
                },
              },
              {
                heading: "Toru",
                description:
                  "Ko te moemoea a Maui kia haere ngatahi ai ratou ko ona tuakana ki te hii ika.",
                image: {
                  alternativeText: "",
                  url: "/images/defaults/image2.jpg",
                  height: 800,
                  width: 800,
                },
              },
              {
                heading: "Rima",
                description:
                  "Ko te moemoea a Maui kia haere ngatahi ai ratou ko ona tuakana ki te hii ika.",
                image: {
                  alternativeText: "",
                  url: "/images/defaults/image1.jpg",
                  height: 800,
                  width: 800,
                },
              },
              {
                heading: "Ono",
                description:
                  "Ko te moemoea a Maui kia haere ngatahi ai ratou ko ona tuakana ki te hii ika.",
                image: {
                  alternativeText: "",
                  url: "/images/defaults/image3.jpg",
                  height: 800,
                  width: 800,
                },
              },
            ],
          }}
        />
      </StyleGuideSection>

      <StyleGuideSection title="Puka" subtitle="Forms" contain>
        <Form className="constrain-width">
          <div className="form-row">
            <Field label="Example Tahi" type="text" name="example-tahi" />
            <Field label="Example Rua" type="text" name="example-rua" />
            <Field label="Example Toru" type="text" name="example-toru" />
          </div>
          <div className="form-row">
            <Field label="Example Wha" type="text" name="example-wha" />
          </div>
          <div className="form-row">
            <Field type="text" name="example-wha" />
          </div>
        </Form>
      </StyleGuideSection>

      <StyleGuideSection title="Whakataka" subtitle="Dropdown" contain>
        <Form className="constrain-width">
          <div className="form-row">
            <Dropdown label="Title" options={["Mr", "Mrs", "Miss", "Other"]} />
            <Dropdown
              label="Education"
              options={[
                "Study",
                "Apprenticeship",
                "Tertiary",
                "Degree",
                "Masters",
                "Trade",
                "Other",
              ]}
            />
          </div>
        </Form>
      </StyleGuideSection>

      <StyleGuideSection
        title="Pātene Irirangi"
        subtitle="Radio Buttons"
        contain
      >
        <div className="constrain-width">
          <RadioColumn label="Gender" text="Wahine" />
          <RadioColumn text="Tane" />
          <RadioColumn text="Ira Weherua-kore" />
          <div className="radio-row">
            <Radio text="Mr" />
            <Radio text="Mrs" />
            <Radio text="Miss" />
          </div>
        </div>
      </StyleGuideSection>

      <StyleGuideSection title="Pōuakataki" subtitle="Checkboxes" contain>
        <div className="constrain-width">
          <p>
            {" "}
            I agree that I have read and accepted the terms and conditions{" "}
          </p>
          <Checkbox text="Āe" />
          <Checkbox text="Kaō" />
        </div>
      </StyleGuideSection>

      <StyleGuideSection title="Rā Whānau" subtitle="Birthdate" contain>
        <Form className="constrain-width">
          <div className="form-row">
            <DatePicker defaultDate={new Date("01/12/2022")} name="birthdate" />
          </div>
        </Form>
      </StyleGuideSection>

      <StyleGuideSection
        title="Rapu Wāhi Noho"
        subtitle="Address Finder"
        contain
      >
        <Form className="constrain-width">
          <AddressFinder onChange={() => {}} />
        </Form>
      </StyleGuideSection>

      <StyleGuideSection title="Papa Waitohu" subtitle="Signature Pad" contain>
        <Form className="constrain-width">
          <SignatureCanvas text="Please sign on the signature pad" />
        </Form>
      </StyleGuideSection>

      <StyleGuideSection title="Dynamic" subtitle="Field" contain>
        <Form className="constrain-width">
          <Whakapapa onChange={() => {}} />
        </Form>
      </StyleGuideSection>
    </div>
  );
};
